import { useState } from "react";
import { Link, useLocation } from "wouter";
import {
  Building2, Users, CreditCard, MessageSquare, BarChart,
  Settings, Database, FileText, Wrench, MessageCircle,
  Menu, Shield, Calendar, TrendingDown, ClipboardList,
  Bell, LayoutDashboard, Sparkles, LogOut, ChevronRight, Scale
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useLanguage } from "@/hooks/use-language";
import { useAuth } from "@/hooks/use-auth";

interface NavItem {
  nameEn: string;
  nameAr: string;
  href: string;
  icon: any;
  key: string;
  badge?: string;
  badgeColor?: string;
}

interface NavSection {
  titleEn: string;
  titleAr: string;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    titleEn: "Overview & Assets",
    titleAr: "الرئيسية والمحفظة العقارية",
    items: [
      { nameEn: "Dashboard", nameAr: "لوحة التحكم", href: "/", icon: LayoutDashboard, key: "dashboard" },
      { nameEn: "Tenants Directory", nameAr: "المستأجرين", href: "/tenants", icon: Users, key: "tenants" },
      { nameEn: "Units & Rooms", nameAr: "الوحدات والغرف", href: "/properties", icon: Building2, key: "properties" },
      { nameEn: "Reservations", nameAr: "طلبات الحجز", href: "/admin/reservations", icon: Calendar, key: "reservations" },
    ],
  },
  {
    titleEn: "Financial Operations",
    titleAr: "العمليات المالية والتحصيل",
    items: [
      { nameEn: "Payments & Rent", nameAr: "المدفوعات والإيجارات", href: "/payment", icon: CreditCard, key: "payments" },
      { nameEn: "Invoices & Expenses", nameAr: "الفواتير والمصروفات", href: "/invoices", icon: FileText, key: "invoices" },
      { nameEn: "Debts & Balances", nameAr: "إدارة الديون والمتأخرات", href: "/debt-management", icon: TrendingDown, key: "debt-management" },
      { nameEn: "Cash Reconciliation", nameAr: "مطابقة الكاش والتوريدات", href: "/cash-reconciliation", icon: Scale, key: "cash-reconciliation" },
    ],
  },
  {
    titleEn: "Automation & AI",
    titleAr: "الأتمتة والذكاء الاصطناعي",
    items: [
      { nameEn: "WhatsApp AI Agent", nameAr: "وكيل WhatsApp الذكي", href: "/whatsapp-ai", icon: Sparkles, key: "whatsapp-ai", badge: "AI", badgeColor: "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20" },
      { nameEn: "WhatsApp & Meta Suite", nameAr: "محادثات ميتا وواتساب", href: "/meta-inbox", icon: MessageSquare, key: "meta-inbox", badge: "Meta", badgeColor: "bg-blue-500/10 text-blue-600 border border-blue-500/20" },
      { nameEn: "Broadcast & SMS", nameAr: "مراسلات الواتساب وSMS", href: "/whatsapp", icon: MessageCircle, key: "whatsapp" },
      { nameEn: "Scheduled Alerts", nameAr: "التنبيهات المجدولة", href: "/scheduled-alerts", icon: Bell, key: "scheduled-alerts" },
    ],
  },
  {
    titleEn: "Platform & Governance",
    titleAr: "التقارير وإدارة النظام",
    items: [
      { nameEn: "Reports & Analytics", nameAr: "التقارير والتحليلات", href: "/reports", icon: BarChart, key: "reports" },
      { nameEn: "Company Settings", nameAr: "إعدادات المنصة والشركة", href: "/company-settings", icon: Settings, key: "company-settings" },
      { nameEn: "Team & Access Roles", nameAr: "فريق العمل والصلاحيات", href: "/team", icon: Shield, key: "team" },
      { nameEn: "Audit Trail & Logs", nameAr: "سجل العمليات والنسخ", href: "/audit-log", icon: ClipboardList, key: "audit-log" },
    ],
  },
];

interface SidebarContentProps {
  onLinkClick?: () => void;
}

export function SidebarContent({ onLinkClick }: SidebarContentProps) {
  const [location] = useLocation();
  const { language, dir } = useLanguage();
  const { user, logout } = useAuth();
  const isAr = language === "ar";

  const companyName = user?.company?.name || "Rently";
  const companyTagline = user?.company?.headerTagline || (isAr ? "نظام إدارة العقارات السحابي" : "Cloud Real Estate Platform");
  const logoUrl = user?.company?.logoUrl;

  const initials = (user?.fullName || "").trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "AD";

  return (
    <div 
      dir={dir} 
      className={cn(
        "flex flex-col h-full bg-zinc-950 text-zinc-200 select-none",
        isAr ? "border-l border-zinc-800/80" : "border-r border-zinc-800/80"
      )}
    >
      {/* Brand Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-zinc-800/80 bg-zinc-950/90">
        <Link href="/" className={cn("flex items-center gap-3 group w-full", isAr ? "text-right" : "text-left")} onClick={onLinkClick}>
          {logoUrl ? (
            <div className="h-10 w-10 rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0 group-hover:border-zinc-600 transition-colors shadow-sm">
              <img src={logoUrl} alt={companyName} className="h-full w-full object-contain p-1" />
            </div>
          ) : (
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 group-hover:scale-105 transition-transform shrink-0">
              <Building2 className="h-5 w-5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h1 className="text-sm font-bold text-white tracking-tight leading-tight truncate">
                {companyName}
              </h1>
              <span className="text-[9px] bg-indigo-500/15 text-indigo-300 font-extrabold px-1.5 py-0.5 rounded border border-indigo-500/30 shrink-0">
                PRO
              </span>
            </div>
            <p className="text-[10px] text-zinc-400 font-medium truncate mt-0.5">
              {companyTagline}
            </p>
          </div>
        </Link>
      </div>

      {/* Navigation Scroll Area */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5 custom-scrollbar">
        {navigationSections.map((section) => (
          <div key={section.titleEn} className="space-y-1">
            <p className={cn(
              "px-2.5 text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1",
              isAr ? "text-right" : "text-left"
            )}>
              <span>{isAr ? section.titleAr : section.titleEn}</span>
            </p>
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
              
              return (
                <Link
                  key={item.key}
                  href={item.href}
                  onClick={onLinkClick}
                  className={cn(
                    "group flex items-center justify-between px-3 py-2 text-xs rounded-xl transition-all duration-150 font-medium",
                    isAr ? "text-right" : "text-left",
                    isActive
                      ? "bg-indigo-600 text-white font-semibold shadow-sm shadow-indigo-600/30"
                      : "text-zinc-300 hover:bg-zinc-900/90 hover:text-white"
                  )}
                  data-testid={`nav-link-${item.key}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Icon className={cn(
                      "h-4 w-4 shrink-0 transition-colors",
                      isActive ? "text-white" : "text-zinc-400 group-hover:text-zinc-200"
                    )} />
                    <span className="truncate">{isAr ? item.nameAr : item.nameEn}</span>
                  </div>
                  {item.badge && (
                    <span className={cn(
                      "text-[9px] px-1.5 py-0.5 rounded-md font-bold tracking-wider",
                      isActive ? "bg-white/20 text-white" : (item.badgeColor || "bg-zinc-800 text-zinc-300")
                    )}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {/* User Footer Card */}
      <div className="shrink-0 border-t border-zinc-800/80 p-3 bg-zinc-950/80">
        <div className="flex items-center gap-2.5 p-1.5 rounded-xl bg-zinc-900/60 border border-zinc-800/60">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-xs">
            {initials}
          </div>
          <div className={cn("flex-1 min-w-0", isAr ? "text-right" : "text-left")}>
            <p className="text-xs font-bold text-white truncate">{user?.fullName || (isAr ? "المدير" : "Super Admin")}</p>
            <p className="text-[10px] text-zinc-400 truncate">
              {user?.roleLabel || user?.role || "Administrator"}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="h-7 w-7 text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg shrink-0 transition-colors"
            title={isAr ? "تسجيل الخروج" : "Sign out"}
            data-testid="button-logout"
          >
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ResponsiveSidebarProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

export function ResponsiveSidebar({ mobileMenuOpen, setMobileMenuOpen }: ResponsiveSidebarProps) {
  const { language } = useLanguage();
  const isAr = language === "ar";

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent />
        </div>
      </div>

      {/* Mobile sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side={isAr ? "right" : "left"} className="p-0 w-72 bg-zinc-950 border-zinc-800">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation Menu</SheetTitle>
          </SheetHeader>
          <SidebarContent onLinkClick={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
}

export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  const { language } = useLanguage();
  const isAr = language === "ar";

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "px-2.5 text-slate-600 md:hidden hover:bg-slate-100",
        isAr ? "border-l border-slate-200" : "border-r border-slate-200"
      )}
      data-testid="mobile-menu-button"
      onClick={onClick}
    >
      <Menu className="h-5 w-5" />
    </Button>
  );
}
